import {
  Home,
  ShoppingCart,
  ChefHat,
  Utensils,
  Package,
  Warehouse,
  ClipboardList,
  ClipboardCheck,
  FileText,
  Truck,
  Users,
  UserCog,
  Receipt,
  CreditCard,
  BarChart3,
  Wallet,
  Settings,
} from 'lucide-react';
import { isRestaurantBusinessType } from './restaurantMode';
import { getAppNavPathEntries } from '../routing/appRouteCatalog';

export {
  getAppNavPathEntries,
  getFirstAllowedAppPath,
  isNavPathActive,
  SHELL_KEEPALIVE_PATHS,
  MANAGER_IDLE_PREFETCH_PATHS,
  WORKER_IDLE_PREFETCH_PATHS,
} from '../routing/appRouteCatalog';

const NAV_ICONS = {
  dashboard: Home,
  sales: ShoppingCart,
  restaurant: ChefHat,
  cuisine: Utensils,
  inventory: Package,
  stock: Warehouse,
  inventur: ClipboardCheck,
  purchases: ClipboardList,
  orders: FileText,
  suppliers: Truck,
  customers: Users,
  mitarbeiter: UserCog,
  expenses: Receipt,
  payments: CreditCard,
  paymentReport: Wallet,
  reports: BarChart3,
  settings: Settings,
};

/** Sidebar section keys — labels via layoutShell.navGroup* */
export const NAV_GROUP_ORDER = ['commerce', 'stock', 'finance', 'admin'];

/** Presentation only — paths/permissions come from appRouteCatalog. */
const NAV_PRESENTATION = {
  '/': { labelKey: 'dashboard', iconKey: 'dashboard', group: 'commerce' },
  '/sales': { labelKey: 'sales', iconKey: 'sales', group: 'commerce' },
  '/cuisine': {
    labelKey: 'layoutShell.navCuisineMonitor',
    iconKey: 'cuisine',
    group: 'commerce',
  },
  '/restaurant': {
    labelKey: 'layoutShell.navKitchenStock',
    iconKey: 'restaurant',
    group: 'commerce',
  },
  '/orders': { labelKey: 'orders', iconKey: 'orders', group: 'commerce' },
  '/customers': { labelKey: 'customers', iconKey: 'customers', group: 'commerce' },
  '/suppliers': { labelKey: 'suppliers', iconKey: 'suppliers', group: 'stock' },
  '/inventory': { labelKey: 'inventory', iconKey: 'inventory', group: 'stock' },
  '/purchases': {
    labelKey: 'purchases',
    restaurantLabelKey: 'layoutShell.navPurchasesIngredients',
    iconKey: 'purchases',
    group: 'stock',
  },
  '/stock': { labelKey: 'stock', iconKey: 'stock', group: 'stock' },
  '/inventur': {
    labelKey: 'layoutShell.navInventur',
    iconKey: 'inventur',
    group: 'stock',
  },
  '/expenses': { labelKey: 'expenses', iconKey: 'expenses', group: 'finance' },
  '/payments': { labelKey: 'payments', iconKey: 'payments', group: 'finance' },
  '/payment-report': {
    labelKey: 'paymentReportPage.title',
    iconKey: 'paymentReport',
    group: 'finance',
  },
  '/reports': { labelKey: 'reports', iconKey: 'reports', group: 'finance' },
  '/mitarbeiter': {
    labelKey: 'layoutShell.navEmployees',
    iconKey: 'mitarbeiter',
    group: 'admin',
    key: 'mitarbeiter',
  },
  '/settings': {
    labelKey: 'settings',
    iconKey: 'settings',
    group: 'admin',
    key: 'settings',
  },
};

/**
 * Navigation abhängig vom Business-Typ.
 * Groups: Commerce → Stock → Finance → Admin (same icons/paths as before).
 */
export function buildAppNavItems({ t, currentLanguage, businessType, hasPermission }) {
  void currentLanguage;
  const isRestaurant = isRestaurantBusinessType(businessType);

  return getAppNavPathEntries(businessType)
    .map(({ path, permission }) => {
      const meta = NAV_PRESENTATION[path];
      if (!meta) return null;
      const labelKey =
        isRestaurant && meta.restaurantLabelKey ? meta.restaurantLabelKey : meta.labelKey;
      return {
        path,
        permission,
        label: t(labelKey),
        icon: NAV_ICONS[meta.iconKey],
        group: meta.group || 'commerce',
        key: meta.key || path,
      };
    })
    .filter(Boolean)
    .filter((item) => hasPermission(item.permission));
}

/** Group flat nav items for sidebar sections (skips empty groups). */
export function groupAppNavItems(navItems = []) {
  const byGroup = new Map(NAV_GROUP_ORDER.map((g) => [g, []]));
  navItems.forEach((item) => {
    const key = NAV_GROUP_ORDER.includes(item.group) ? item.group : 'commerce';
    byGroup.get(key).push(item);
  });
  return NAV_GROUP_ORDER.filter((g) => byGroup.get(g).length > 0).map((group) => ({
    group,
    items: byGroup.get(group),
  }));
}
